import React from "react";
import PolicyLayout, { Section } from "@/components/PolicyLayout";

const SECTIONS = [
  { id: "intro", label: "1. Overview" },
  { id: "windows", label: "2. Return Windows by Category" },
  { id: "eligibility", label: "3. Eligibility" },
  { id: "non-returnable", label: "4. Non-Returnable Items" },
  { id: "process", label: "5. How to Raise a Return" },
  { id: "pickup", label: "6. Pickup & Reverse Logistics" },
  { id: "quality", label: "7. Quality Check" },
  { id: "damaged", label: "8. Damaged / Wrong / Missing" },
  { id: "cancellations", label: "9. Cancellations" },
  { id: "vendor-role", label: "10. Vendor Responsibility" },
  { id: "grievance", label: "11. Grievance" },
];

export default function ReturnPolicy() {
  return (
    <PolicyLayout
      title="Return Policy"
      tagline="Florvanta is a marketplace; returns are processed in coordination with the Vendor who fulfilled your order. Please review the applicable return window before purchase."
      updated="February 2026"
      sections={SECTIONS}
    >
      <Section id="intro" title="1. Overview">
        <p>
          This Return Policy is issued in accordance with Section 5(3)(b) of the Consumer Protection (E-Commerce) Rules, 2020, which requires marketplace e-commerce entities to make available clear and accessible information regarding returns.
        </p>
      </Section>

      <Section id="windows" title="2. Return Windows by Category">
        <div className="overflow-auto rounded-lg border border-[#E2E8E5]">
          <table className="w-full text-sm">
            <thead className="bg-[#E8F5F2] text-[#0F3D2E]">
              <tr>
                <th className="p-3 text-left">Category</th>
                <th className="p-3 text-left">Return Window</th>
                <th className="p-3 text-left">Type</th>
                <th className="p-3 text-left">Conditions</th>
              </tr>
            </thead>
            <tbody className="text-[#0F3D2E]">
              <tr className="border-t border-[#E2E8E5]">
                <td className="p-3">Clothing &amp; Accessories</td>
                <td className="p-3">7 days from delivery</td>
                <td className="p-3">Return / Exchange</td>
                <td className="p-3">Unworn, unwashed, tags intact, original packaging</td>
              </tr>
              <tr className="border-t border-[#E2E8E5]">
                <td className="p-3">Electronics</td>
                <td className="p-3">7 days from delivery</td>
                <td className="p-3">Return / Replacement</td>
                <td className="p-3">Sealed accessories, no physical damage, all invoices &amp; boxes</td>
              </tr>
              <tr className="border-t border-[#E2E8E5]">
                <td className="p-3">Grocery — Perishable</td>
                <td className="p-3">Report within 24 hours</td>
                <td className="p-3">Replacement / Refund only</td>
                <td className="p-3">Damaged / spoilt / short-quantity on arrival, with unboxing evidence</td>
              </tr>
              <tr className="border-t border-[#E2E8E5]">
                <td className="p-3">Grocery — Packaged / Non-perishable</td>
                <td className="p-3">3 days from delivery</td>
                <td className="p-3">Replacement / Refund</td>
                <td className="p-3">Unopened, seal intact, well within expiry date</td>
              </tr>
            </tbody>
          </table>
        </div>
        <p className="text-xs text-[#6E877C]">Vendors may offer longer windows on specific listings; the longer of the two shall apply.</p>
      </Section>

      <Section id="eligibility" title="3. Eligibility">
        <p>A product is eligible for return if all of the following are true:</p>
        <ul className="list-disc pl-5 space-y-1">
          <li>The request is raised within the applicable return window above.</li>
          <li>The item is unused, in its original condition, with all tags, manuals, warranty cards, and accessories.</li>
          <li>The original invoice and packaging are available.</li>
          <li>The item does not appear on the Non-Returnable list below.</li>
        </ul>
      </Section>

      <Section id="non-returnable" title="4. Non-Returnable Items">
        <ul className="list-disc pl-5 space-y-1">
          <li>Innerwear, lingerie, socks and swimwear (hygiene reasons).</li>
          <li>Cosmetics, personal care, and any item with a broken seal.</li>
          <li>Perishable grocery once the 24-hour report window has lapsed.</li>
          <li>Digital products, software downloads, and prepaid vouchers.</li>
          <li>Made-to-order, personalised, or custom-tailored items.</li>
          <li>Items marked &quot;Non-Returnable&quot; on the product page.</li>
        </ul>
      </Section>

      <Section id="process" title="5. How to Raise a Return">
        <ol className="list-decimal pl-5 space-y-1">
          <li>Sign in and go to <b>My Orders</b>.</li>
          <li>Select the delivered item and click <b>Request Return</b>.</li>
          <li>Choose the reason and upload photos / short video where applicable.</li>
          <li>Submit. You will receive a reference number and an acknowledgement email/SMS.</li>
          <li>Vendor accepts / rejects the request within 48 hours; if not actioned, Florvanta may auto-approve valid requests.</li>
        </ol>
      </Section>

      <Section id="pickup" title="6. Pickup &amp; Reverse Logistics">
        <p>
          Once approved, our logistics partner will schedule a reverse pickup within 3-5 working days from the delivery address. Please keep the item packed with all original accessories. Two pickup attempts will be made; failure on both attempts may result in the return being closed.
        </p>
        <p>
          For non-serviceable pincodes, self-shipping instructions will be shared; approved shipping costs will be reimbursed.
        </p>
      </Section>

      <Section id="quality" title="7. Quality Check">
        <p>
          Returned items undergo a quality check at the Vendor&apos;s warehouse. If the returned item is materially different, damaged (not reported), or not in its original condition, the return may be rejected and the item shipped back to you. In such cases the reverse shipping cost may be recovered from the refund.
        </p>
      </Section>

      <Section id="damaged" title="8. Damaged, Wrong or Missing Items">
        <p>
          If you receive a damaged, wrong, or missing item, please report it within <b>24 hours</b> of delivery with clear unboxing photographs / a short video. Such cases are prioritised and refund/replacement is initiated on first-attempt QC.
        </p>
      </Section>

      <Section id="cancellations" title="9. Order Cancellations">
        <p>
          You may cancel an order any time before it is marked <b>Shipped</b>, free of charge, from the <b>My Orders</b> page. Once shipped, the order can only be returned as per the applicable window above.
        </p>
      </Section>

      <Section id="vendor-role" title="10. Vendor Responsibility">
        <p>
          The Vendor listed on your invoice is the legal seller and is responsible for accepting valid returns, issuing replacements, and complying with warranty and consumer protection obligations. Florvanta facilitates the process and may intervene in disputes.
        </p>
      </Section>

      <Section id="grievance" title="11. Grievance">
        <p>
          If you are not satisfied with the resolution, write to <b>grievance@florvanta.co.in</b> quoting the return reference number.
        </p>
      </Section>
    </PolicyLayout>
  );
}
